<template>
  <div>
    <div v-if="loading">
      <LoadingState />
    </div>

    <div v-else>
      <!-- Hero / Header -->
      <div
        class="bg-gradient-to-tr from-blue-600 via-sky-600 to-indigo-500 h-64 pt-[56px] w-full flex items-center dark:bg-gradient-to-tr dark:from-gray-900 dark:via-slate-800 dark:to-gray-900"
      >
        <div class="container mx-auto px-5">
          <div
            class="2xl:border-l-4 xl:border-l-4 lg:border-l-4 md:border-l-4 border-white dark:border-lime-600 2xl:text-start xl:text-start md:text-start text-center"
          >
            <h1 class="font-bold text-4xl text-white mb-2 indent-2 dark:text-lime-500">
              {{ $t('career.vacancy') }}
            </h1>
          </div>

          <button
            type="button"
            @click="$router.back()"
            class="text-white dark:text-lime-500 2xl:hidden xl:hidden lg:hidden md:hidden inline-flex items-center font-medium hover:text-gray-900 dark:hover:text-white cursor-pointer justify-center w-full"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-4 h-4 mr-2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3"
              />
            </svg>
            <span>{{ $t('table.go-back') }}</span>
          </button>

          <nav aria-label="Breadcrumb" class="2xl:block xl:block lg:block md:block hidden">
            <ol class="flex space-x-1 md:space-x-3 text-white dark:text-lime-500">
              <li class="flex items-center hover:text-gray-900 dark:hover:text-white cursor-pointer">
                <button
                  type="button"
                  @click="$router.push('/')"
                  class="inline-flex items-center font-medium"
                >
                  <svg
                    class="w-4 h-4 mr-2"
                    fill="white"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"
                    />
                  </svg>
                  <span class="hidden sm:block">{{ $t('ceo-message.main-screen') }}</span>
                </button>
              </li>

              <li class="flex items-center">
                <svg class="w-6 h-6 text-gray-400" fill="white" viewBox="0 0 20 20">
                  <path
                    fill-rule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clip-rule="evenodd"
                  />
                </svg>

                <button
                  type="button"
                  @click="$router.push('/careers')"
                  class="ml-1 md:ml-2 font-medium hover:underline"
                >
                  {{ $t('navbar.career') }}
                </button>
              </li>

              <li aria-current="page">
                <div class="flex items-center">
                  <svg
                    class="w-6 h-6 text-gray-400"
                    fill="white"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  <span class="ml-1 font-medium md:ml-2 select-none">
                    {{ $t('career.vacancy') }}
                  </span>
                </div>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      <!-- Main Content -->
      <main class="container mx-auto px-5 my-8">
        <!-- Vacancies Section -->
        <section
          class="border dark:border-lime-600 bg-slate-50/50 dark:bg-gray-900 rounded-3xl shadow-sm overflow-hidden"
        >
          <div class="dark:bg-gray-900">
            <div class="container mx-auto flex justify-center dark:text-lime-400">
              <p class="font-bold text-xl pb-5 pt-5 dark:text-white">
                {{ $t('vacancy.current-vacancies') }}
              </p>
            </div>
          </div>

          <div class="dark:bg-gray-900 pb-8">
            <div
              v-if="vacancies.length === 0"
              class="px-5 2xl:px-20 xl:px-10 lg:px-10 md:px-10"
            >
              <div
                class="rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 p-8 text-center"
              >
                <p class="text-gray-500 dark:text-gray-300">
                  Hozircha mavjud bo‘sh ish o‘rinlari topilmadi.
                </p>
              </div>
            </div>

            <div v-else class="space-y-4">
              <div v-for="(vacancy, index) in vacancies" :key="vacancy.id || index">
                <div class="container mx-auto 2xl:px-20 xl:px-10 lg:px-10 md:px-10 sm:px-5 px-5">
                  <div class="border border-gray-200 dark:border-lime-600 rounded-2xl overflow-hidden shadow-sm bg-white dark:bg-gray-800">
                    <button
                      type="button"
                      @click="toggleAccordion(index)"
                      class="w-full cursor-pointer flex justify-between items-center gap-4 text-left text-white p-4 font-bold bg-blue-600 hover:bg-blue-700 dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-lime-400 transition"
                    >
                      <span>
                        {{ getLocalized(vacancy, 'departmentName') }}
                        <span v-if="getLocalized(vacancy, 'departmentNameAbout')">
                          -
                          {{ getLocalized(vacancy, 'departmentNameAbout') }}
                        </span>
                      </span>

                      <svg
                        class="h-6 w-6 shrink-0 transition-transform duration-300"
                        :class="{ 'rotate-180': isAccordionOpen(index) }"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>

                    <transition name="fade">
                      <div
                        v-show="isAccordionOpen(index)"
                        class="p-5 rounded-b-2xl bg-gray-50 dark:bg-gray-900 dark:border-lime-500"
                        role="alert"
                      >
                        <div class="lg:flex justify-between gap-6 sm:flex-wrap">
                          <div class="flex-initial lg:w-1/2 sm:w-full">
                            <h2 class="font-bold text-gray-700 dark:text-white text-xl">
                              {{ getLocalized(vacancy, 'title') }}
                            </h2>
                          </div>

                          <div class="flex-nowrap lg:w-1/3 sm:w-full mt-4 lg:mt-0">
                            <h2 class="font-medium text-blue-600 dark:text-white pb-2">
                              {{ $t('vacancy.aplicationDeadline') }}:
                              {{ formattedDate(vacancy.expireDate) }}
                              {{ $t('vacancy.ended-text') }}
                            </h2>

                            <h2 class="font-medium text-blue-600 dark:text-white pb-2">
                              {{ $t('vacancy.aplication-testing') }}:
                              {{ formattedDate(vacancy.dateOfTesting) }}
                            </h2>
                          </div>
                        </div>

                        <div class="flex mt-6">
                          <button
                            v-if="isTestingAvailable(vacancy.dateOfTesting)"
                            type="button"
                            class="inline-flex items-center gap-2 rounded-xl bg-blue-600 hover:bg-blue-700 dark:bg-lime-600 dark:hover:bg-lime-700 text-white px-5 py-2.5 text-sm font-semibold transition"
                            @click="goTo(index)"
                          >
                            {{ $t('home-page.watch') }}
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3" />
                            </svg>
                          </button>

                          <button
                            v-else
                            type="button"
                            class="inline-flex items-center gap-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 dark:bg-lime-600 dark:hover:bg-lime-700 text-white px-5 py-2.5 text-sm font-semibold transition"
                            @click="goToResult(index)"
                          >
                            {{ $t('home-page.watch-result') }}
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </transition>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Interview Questions Section -->
        <section
          class="mt-10 rounded-3xl border border-blue-100 dark:border-lime-600 bg-white dark:bg-gray-900 shadow-xl overflow-hidden"
        >
          <!-- Questions Header -->
          <div
            class="bg-gradient-to-r from-blue-600 via-sky-600 to-indigo-600 dark:from-gray-900 dark:via-slate-800 dark:to-gray-900 p-6 md:p-8"
          >
            <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
              <div>
                <p class="text-blue-100 dark:text-lime-300 text-sm font-semibold uppercase tracking-wider">
                  {{ $t("vacancyQuestions.label") }}
                </p>

                <h2 class="text-2xl md:text-3xl font-bold text-white dark:text-lime-400 mt-2">
                  {{ $t("vacancyQuestions.title") }}
                </h2>

                <p class="text-blue-100 dark:text-gray-300 mt-3 max-w-4xl leading-relaxed">
                  {{ $t("vacancyQuestions.description") }}
                </p>
              </div>

              <div
                class="bg-white/15 dark:bg-lime-400/10 rounded-2xl px-6 py-4 text-white dark:text-lime-300 border border-white/20 dark:border-lime-600"
              >
                <p class="text-sm opacity-90">{{ $t("vacancyQuestions.positions") }}</p>
                <p class="text-3xl font-bold">{{ questionsSections.length }}</p>
              </div>
            </div>
          </div>

          <!-- Search -->
          <div class="p-5 md:p-6 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
            <div class="relative">
              <input
                v-model="questionSearch"
                type="text"
                :placeholder="$t('vacancyQuestions.searchPlaceholder')"
                class="w-full rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-5 py-4 pr-12 text-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-lime-500"
              />

              <svg
                class="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-lime-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="m21 21-4.35-4.35M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15z"
                />
              </svg>
            </div>
          </div>

          <!-- Questions Accordion -->
          <div class="p-5 md:p-6 space-y-4">
            <div
              v-for="section in filteredQuestionSections"
              :key="section.key"
              class="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden shadow-sm hover:shadow-md transition"
            >
              <button
                type="button"
                @click="toggleQuestionSection(section.key)"
                class="w-full flex items-center justify-between gap-4 p-5 text-left bg-gray-50 hover:bg-blue-50 dark:bg-gray-800 dark:hover:bg-gray-700 transition"
              >
                <div class="flex items-start gap-4">
                  <div
                    class="shrink-0 w-11 h-11 rounded-2xl bg-blue-100 dark:bg-lime-400/10 flex items-center justify-center"
                  >
                    <svg
                      class="w-6 h-6 text-blue-600 dark:text-lime-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.008v.008H3.75V6.75Zm0 5.25h.008v.008H3.75V12Zm0 5.25h.008v.008H3.75v-.008Z"
                      />
                    </svg>
                  </div>

                  <div>
                    <h3 class="font-bold text-gray-800 dark:text-white text-base md:text-lg leading-snug mt-2">
                      {{ section.title }}
                    </h3>
                  </div>
                </div>

                <svg
                  class="w-6 h-6 text-gray-500 dark:text-lime-400 transition-transform duration-300 shrink-0"
                  :class="{ 'rotate-180': isQuestionOpen(section.key) }"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="m19 9-7 7-7-7"
                  />
                </svg>
              </button>

              <transition name="fade">
                <div v-show="isQuestionOpen(section.key)" class="p-5 md:p-6 bg-white dark:bg-gray-900">
                  <ol class="space-y-3">
                    <li
                      v-for="(question, questionIndex) in section.questions"
                      :key="questionIndex"
                      class="group flex gap-4 rounded-2xl border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-4 hover:border-blue-300 dark:hover:border-lime-500 transition"
                    >
                      <span
                        class="shrink-0 w-8 h-8 rounded-xl bg-blue-600 dark:bg-lime-600 text-white flex items-center justify-center text-sm font-bold"
                      >
                        {{ questionIndex + 1 }}
                      </span>

                      <p class="text-gray-700 dark:text-gray-200 leading-relaxed">
                        {{ question }}
                      </p>
                    </li>
                  </ol>
                </div>
              </transition>
            </div>

            <div
              v-if="filteredQuestionSections.length === 0"
              class="text-center py-10 text-gray-500 dark:text-gray-400"
            >
              {{ $t("vacancyQuestions.emptySearch") }}
            </div>
          </div>
        </section>
      </main>
    </div>
  </div>
</template>

<script>
import LoadingState from "@/components/LoadingState";
import axios from "axios";

export default {
  name: "VacanciesPage",

  components: {
    LoadingState,
  },

  data() {
    return {
      vacancies: [],
      loading: true,
      openIndex: null,
      today: new Date(),

      questionSearch: "",
      openQuestionKey: null,
    };
  },

  computed: {
    questionsSections() {
      const sections = this.$tm ? this.$tm("vacancyQuestions.sections") : {};

      if (!sections || typeof sections !== "object") {
        return [];
      }

      return Object.keys(sections).map((key) => {
        const section = sections[key];

        return {
          key,
          title: this.$rt ? this.$rt(section.title) : section.title,
          questions: Array.isArray(section.questions)
            ? section.questions.map((question) =>
                this.$rt ? this.$rt(question) : question
              )
            : [],
        };
      });
    },

    filteredQuestionSections() {
      const search = this.questionSearch.toLowerCase().trim();

      if (!search) {
        return this.questionsSections;
      }

      return this.questionsSections
        .map((section) => {
          const titleMatched = section.title.toLowerCase().includes(search);

          const questions = titleMatched
            ? section.questions
            : section.questions.filter((question) =>
                question.toLowerCase().includes(search)
              );

          return {
            ...section,
            questions,
          };
        })
        .filter((section) => section.questions.length > 0);
    },
  },

  methods: {
    loadTable() {
      this.loading = true;

      axios
        .get("/api/Website/Vacancy/GetVacancies")
        .then((response) => {
          this.vacancies = Array.isArray(response.data) ? response.data : [];
        })
        .catch((error) => {
          console.log(error);
          this.vacancies = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },

    getLocalized(item, key) {
      if (!item) return "";

      const locale = this.$i18n && this.$i18n.locale ? this.$i18n.locale : "uz";

      const suffixMap = {
        ru: "Ru",
        uz: "Uz",
        en: "En",
      };

      const suffix = suffixMap[locale] || "Uz";

      return (
        item[`${key}${suffix}`] ||
        item[`${key}Uz`] ||
        item[`${key}Ru`] ||
        item[`${key}En`] ||
        ""
      );
    },

    formattedDate(value) {
      if (!value) return "—";

      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return "—";
      }

      return date.toLocaleDateString("ru-RU");
    },

    toStartOfDay(value) {
      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return null;
      }

      date.setHours(0, 0, 0, 0);

      return date;
    },

    isTestingAvailable(dateOfTesting) {
      const testingDate = this.toStartOfDay(dateOfTesting);
      const today = this.toStartOfDay(this.today);

      if (!testingDate || !today) {
        return true;
      }

      return testingDate >= today;
    },

    goTo(index) {
      sessionStorage.setItem("VACANCY", JSON.stringify(this.vacancies[index]));
      this.$router.push("/instructions");
    },

    goToResult(index) {
      sessionStorage.setItem("VACANCY_ID", JSON.stringify(this.vacancies[index].id));
      this.$router.push("/vacancyResults");
    },

    toggleAccordion(index) {
      this.openIndex = this.openIndex === index ? null : index;
    },

    isAccordionOpen(index) {
      return this.openIndex === index;
    },

    toggleQuestionSection(key) {
      this.openQuestionKey = this.openQuestionKey === key ? null : key;
    },

    isQuestionOpen(key) {
      return this.openQuestionKey === key;
    },
  },

  mounted() {
    this.loadTable();
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: all 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
